
/**
 * Checkout. AC-1.3 and AC-4.1.
 *
 * Buying works logged out right up to this page; the account is created inline
 * here without losing the cart, because the cart lives in the browser and
 * nothing about it depends on the session.
 *
 * The Payment Element mounts here when Stripe keys are configured. Without them
 * the same request goes to /api/checkout, which prices the cart identically and
 * exercises the whole post-payment pipeline, and the page says which mode it is
 * in rather than pretending a card was charged.
 */

import { LockSimple } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useCart } from '@/components/cart-provider';
import { useCartPricing } from '@/components/cart/cart-view';
import { Button, ButtonLink, Callout, EmptyState, Field, inputClass } from '@/components/ui';
import { formatCents } from '@/lib/money';

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

type Errors = Partial<Record<'name' | 'email' | 'line1' | 'suburb' | 'postcode' | 'form' | 'promo', string>>;

export function CheckoutView({
  signedInEmail,
  stripeConfigured,
}: {
  signedInEmail: string | null;
  stripeConfigured: boolean;
}) {
  const navigate = useNavigate();
  const { lines, clear } = useCart();
  const { priced, error: pricingError, loading, ready } = useCartPricing();

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  if (!ready || loading) {
    return <div className="h-96 animate-pulse rounded-[10px] bg-panel" />;
  }

  if (pricingError) {
    return <Callout tone="danger" title={pricingError} />;
  }

  if (!priced || priced.lines.length === 0) {
    return (
      <EmptyState
        title="Nothing to check out"
        body="Your cart is empty. Add something from the store first."
        action={<ButtonLink href="/shop">Browse the store</ButtonLink>}
      />
    );
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const get = (k: string) => String(form.get(k) ?? '').trim();

    const next: Errors = {};
    const email = signedInEmail ?? get('email');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      next.email = 'Use an email address we can send the receipt to.';
    }
    if (!get('name')) next.name = 'We need a name to put on the parcel.';
    if (!get('line1')) next.line1 = 'We need a street address to ship to.';
    if (!get('suburb')) next.suburb = 'Add the suburb.';
    if (!/^\d{4}$/.test(get('postcode'))) next.postcode = 'Australian postcodes are 4 digits.';

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      // Account creation happens inline when checking out logged out, and it
      // does not touch the cart.
      if (!signedInEmail) {
        await fetch('/api/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
      }

      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'web',
          items: lines,
          shipping_address: {
            name: get('name'),
            email: signedInEmail || email || undefined,
            line1: get('line1'),
            line2: get('line2') || undefined,
            suburb: get('suburb'),
            state: get('state'),
            postcode: get('postcode'),
          },
          promo_code: get('promo_code') || undefined,
        }),
      });
      const body = await res.json();

      if (!res.ok) {
        if (body?.error?.code === 'APP_ONLY_PROMO') {
          setErrors({ promo: body.error.message });
        } else {
          setErrors({ form: body?.error?.message ?? 'That did not go through.' });
        }
        return;
      }

      if (body.payment_intent_client_secret) {
        // Stripe path: the Payment Element confirms, the webhook writes the
        // order, and this page waits on the redirect return.
        setErrors({
          form: 'Payment Element is configured. Confirm the payment to complete the order.',
        });
        return;
      }

      clear();
      // The token comes with the reference and is what makes this link work
      // for a guest. Without it the confirmation page is a 404 (R-154).
      navigate({
        to: '/orders/$ref',
        params: { ref: body.ref },
        search: { new: '1', ...(body.token ? { t: body.token } : {}) },
      });
    } catch {
      setErrors({ form: 'That did not go through. Check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
      <div className="max-w-[560px] space-y-8">
        {errors.form && <Callout tone="danger" title={errors.form} />}

        {!stripeConfigured && (
          <Callout tone="warn" title="Stripe is not connected yet">
            {/* Two things this said were no longer true: stock is RESERVED
                rather than decremented, and it stays on the shelf until the
                order ships (R-146), and this contract has no commission ledger
                at all (FR-512). Found by walking the page. R-154. */}
            No card is charged. The order is written, the stock is reserved and the receipt is
            queued, so the whole pipeline behind payment is
            live. Add STRIPE_SECRET_KEY to switch on the Payment Element.
          </Callout>
        )}

        <section>
          <h2 className="text-lg font-bold tracking-[-0.02em]">Contact</h2>
          <div className="mt-4">
            {signedInEmail ? (
              <p className="text-[15px]">
                Signed in as <span className="font-semibold">{signedInEmail}</span>
              </p>
            ) : (
              <Field
                label="Email"
                htmlFor="email"
                error={errors.email}
                hint="We create your account with this and send the receipt to it."
              >
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  className={inputClass}
                  aria-invalid={Boolean(errors.email)}
                />
              </Field>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-bold tracking-[-0.02em]">Shipping address</h2>
          {/* Single column, field widths matched to the input they expect. */}
          <div className="mt-4 space-y-6">
            {/* A parcel needs a name on it. There was no name field at all, and
                every order reached the studio from "Guest". R-145. */}
            <Field label="Full name" htmlFor="name" error={errors.name}>
              <input
                id="name"
                name="name"
                autoComplete="name"
                className={inputClass}
                aria-invalid={Boolean(errors.name)}
              />
            </Field>
            <Field label="Street address" htmlFor="line1" error={errors.line1}>
              <input
                id="line1"
                name="line1"
                autoComplete="address-line1"
                className={inputClass}
                aria-invalid={Boolean(errors.line1)}
              />
            </Field>

            <Field label="Unit or level" htmlFor="line2" optional>
              <input
                id="line2"
                name="line2"
                autoComplete="address-line2"
                className={`${inputClass} max-w-[240px]`}
              />
            </Field>

            <Field label="Suburb" htmlFor="suburb" error={errors.suburb}>
              <input
                id="suburb"
                name="suburb"
                autoComplete="address-level2"
                className={`${inputClass} max-w-[340px]`}
                aria-invalid={Boolean(errors.suburb)}
              />
            </Field>

            <div className="flex flex-wrap gap-4">
              <Field label="State" htmlFor="state">
                <select
                  id="state"
                  name="state"
                  defaultValue="NSW"
                  className={`${inputClass} w-[130px]`}
                >
                  {AU_STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>

              <Field label="Postcode" htmlFor="postcode" error={errors.postcode}>
                <input
                  id="postcode"
                  name="postcode"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={4}
                  className={`${inputClass} w-[120px]`}
                  aria-invalid={Boolean(errors.postcode)}
                />
              </Field>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-bold tracking-[-0.02em]">Promo code</h2>
          <div className="mt-4">
            <Field label="Code" htmlFor="promo_code" optional error={errors.promo}>
              <input
                id="promo_code"
                name="promo_code"
                autoCapitalize="characters"
                className={`${inputClass} max-w-[240px] font-mono uppercase`}
                aria-invalid={Boolean(errors.promo)}
              />
            </Field>
          </div>
        </section>

        <Button type="submit" size="lg" disabled={submitting}>
          <LockSimple size={18} weight="bold" aria-hidden />
          {submitting ? 'Placing order' : `Pay ${formatCents(priced.total_cents)}`}
        </Button>
      </div>

      <aside className="rounded-[10px] border border-line bg-panel p-5 lg:sticky lg:top-24">
        <h2 className="text-sm font-bold">Order summary</h2>
        <ul className="mt-4 space-y-3 border-b border-line pb-4">
          {priced.lines.map((l) => (
            <li key={`${l.published_id}-${l.size}`} className="flex justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block font-semibold">{l.title}</span>
                <span className="text-muted">
                  {l.color_name} &middot; {l.size} &middot; x{l.qty}
                </span>
              </span>
              <span className="shrink-0 font-semibold">{formatCents(l.line_total_cents)}</span>
            </li>
          ))}
        </ul>
        <dl className="mt-4 space-y-2.5 text-[15px]">
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Subtotal</dt>
            <dd className="font-semibold">{formatCents(priced.subtotal_cents)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Shipping</dt>
            <dd className="font-semibold">
              {priced.shipping_cents === 0 ? 'Free' : formatCents(priced.shipping_cents)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-line pt-3 text-lg">
            <dt className="font-bold">Total</dt>
            <dd className="font-extrabold">{formatCents(priced.total_cents)}</dd>
          </div>
          {/* AC-1.4: the GST component is itemised at checkout. */}
          <div className="flex justify-between gap-4">
            <dt className="text-[13px] text-muted">GST included</dt>
            <dd className="text-[13px] text-muted">{formatCents(priced.gst_cents)}</dd>
          </div>
        </dl>
      </aside>
    </form>
  );
}
